import React, {
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  useState,
  forwardRef
} from 'react';
import styled from 'styled-components';
import vegaEmbed from 'vega-embed';
import PropTypes from 'prop-types';

const VegaChartContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: var(--white);
`;

const VegaViewContainer = styled.div`
  display: flex;
  width: 100%;
`;

const config = {
  autosize: {
    type: 'fit-x',
    contains: 'padding'
  },
  background: 'transparent',
  padding: 40,
  font: "'Inter', sans-serif",
  title: {
    color: 'rgba(20,20,20,1.00)',
    anchor: 'start',
    align: 'left',
    fontSize: 20
  },
  legend: {
    titleColor: 'rgba(79,77,68,1.00)',
    labelColor: 'rgba(59,58,57,0.50)',
    rowPadding: 5
  },
  axis: {
    gridColor: '#000',
    gridOpacity: 0.1,
    tickColor: 'rgba(79,77,68,1.00)',
    labelColor: 'rgba(59,58,57,0.50)', // "rgba(79,77,68,1.00)",
    titleColor: 'rgba(79,77,68,1.00)',
    ticks: false,
    titleFontSize: 16,
    titleFontWeight: 600,
    titlePadding: 10,
    labelFontSize: 14,
    labelPadding: 10
  },
  axisY: {
    domainColor: 'transparent',
    ticks: false,
    titleAngle: 0,
    titleAlign: 'start',
    titleAnchor: 'start',
    titleX: 0,
    titleY: -30,
    labelOffset: -10
  },
  axisX: {
    grid: false
  },
  view: {
    stroke: 'transparent'
  },
  range: {
    category: [
      '#EB6F15',
      '#686276',
      '#FBE38E',
      '#CFAE91',
      '#BC5112',
      '#44404C',
      '#F7B928',
      '#88502C'
    ]
  },
  mark: {
    color: '#EB6F15',
    invalid: 'filter'
  },
  bar: {
    width: { band: 0.8 }
  }
};

const VegaChart = forwardRef(({ spec }, forwardedRef) => {
  const vegaRef = useRef(null);
  const vegaInstance = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const resolvedRef = forwardedRef || vegaInstance;

  useImperativeHandle(forwardedRef, () => vegaInstance.current, [vegaInstance.current]);

  // Update container width based on the ref
  const onWindowResize = useCallback(() => {
    if (vegaRef.current) {
      setContainerWidth(vegaRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    onWindowResize(); // Set initial width
    window.addEventListener('resize', onWindowResize);
    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, [onWindowResize]);

  const embedChart = useCallback(async () => {
    if (vegaRef.current && spec && containerWidth > 0) {
      try {
        const updatedSpec = {
          ...spec,
          width: containerWidth,
          height: (containerWidth * 9) / 16
        };

        if (spec.layer?.some((layer) => layer.encoding)) {
          updatedSpec.layer = spec.layer.map((layer) => {
            if (layer.encoding && layer.encoding.x) {
              return {
                ...layer,
                encoding: {
                  ...layer.encoding,
                  x: {
                    ...layer.encoding.x,
                    bandPosition: 0
                  }
                }
              };
            }

            return layer;
          });
        }

        if (spec.encoding && spec.encoding.x) {
          updatedSpec.encoding = {
            ...spec.encoding,
            x: {
              ...spec.encoding.x,
              bandPosition: 0
            }
          };
        }

        resolvedRef.current = await vegaEmbed(vegaRef.current, updatedSpec, {
          mode: 'vega-lite',
          actions: false,
          config: { ...config, autosize: { ...config.autosize, type: 'fit-x' }, padding: 20 }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error embedding Vega chart:', error);
      }
    }
  }, [spec, containerWidth]);

  useEffect(() => {
    embedChart();
  }, [embedChart]);

  return (
    <VegaChartContainer>
      <VegaViewContainer ref={vegaRef} />
    </VegaChartContainer>
  );
});

VegaChart.propTypes = {
  spec: PropTypes.shape({
    title: PropTypes.string,
    layer: PropTypes.arrayOf(
      PropTypes.shape({
        x: PropTypes.shape({}),
        y: PropTypes.shape({})
      })
    ),
    encoding: PropTypes.shape({
      x: PropTypes.shape({}),
      y: PropTypes.shape({})
    })
  }).isRequired
};

export { VegaChart };
